import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, AlertTriangle, Boxes } from "lucide-react";
import { fmtCurrencyExact } from "@/lib/constants";
import {
  FAB_TYPES, FAB_MATERIALS, FAB_FINISHES, INFILL_TYPES, FRAME_ORIENTATIONS, GLASS_THICKNESSES,
  GATE_INFILL_TYPES,
  DESIGN_STYLES, designStylesFor, fabDefault,
} from "@/lib/fabrication";
import FabricationBlueprint from "@/components/FabricationBlueprint";

function Num({ label, value, onChange, step = "1", min = "0", suffix, testid }) {
  return (
    <div>
      <Label className="stamp-label text-slate-600">{label}</Label>
      <div className="relative mt-1">
        <Input type="number" step={step} min={min} value={value ?? 0}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="h-9 rounded-sm font-mono text-sm pr-11" data-testid={testid} />
        {suffix && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">{suffix}</span>}
      </div>
    </div>
  );
}

function Pick({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="stamp-label text-slate-600">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9 rounded-sm text-sm mt-1" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between border border-slate-200 rounded-sm px-3 h-9">
      <Label className="stamp-label text-slate-600">{label}</Label>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

export default function FabricationCalculator({ initial, onChange }) {
  const [input, setInput] = useState(() => ({ ...fabDefault("balustrade"), ...(initial || {}) }));
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  const setType = (t) => setInput((i) => ({ ...fabDefault(t), material: i.material, finish: i.finish, qty: i.qty, notes: i.notes }));
  const upd = (patch) => setInput((i) => ({ ...i, ...patch }));

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/fabrication/calculate", input);
        setResult(data);
        onChangeRef.current?.(input, data);
      } catch (e) {
        setResult(null);
      } finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [input]);

  const t = input.fab_type;

  return (
    <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
      {/* ---------- Inputs ---------- */}
      <Card className="rounded-sm border-slate-200 industrial-shadow p-4 sm:p-5 space-y-4 fab-no-print">
        <div>
          <div className="stamp-label text-cyan-600 mb-2">Fabrication type</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {FAB_TYPES.map((ft) => (
              <button key={ft.id} type="button" onClick={() => setType(ft.id)}
                data-testid={`fab-type-${ft.id}`}
                className={`h-10 rounded-sm border text-xs font-semibold transition-colors ${t === ft.id ? "bg-cyan-600 text-white border-cyan-600" : "bg-white border-slate-200 text-slate-700 hover:border-cyan-400"}`}>
                {ft.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {t !== "glass" && <Pick label="Material" value={input.material} onChange={(v) => upd({ material: v })} options={FAB_MATERIALS} testid="fab-material" />}
          {t !== "glass" && <Pick label="Finish" value={input.finish} onChange={(v) => upd({ finish: v })} options={FAB_FINISHES} testid="fab-finish" />}
        </div>

        {/* Design style + member cross-section — shared across every fab type
            so the workshop knows the exact section to cut. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-slate-100 pt-3">
          <Pick
            label="Design style"
            value={input.design_style || (designStylesFor(t)[0]?.id || "")}
            onChange={(v) => {
              const style = designStylesFor(t).find((s) => s.id === v);
              const patch = { design_style: v };
              // Pre-fill the member size to the style's default the first time
              // it's picked (or if the user is still on the previous default).
              if (style?.member_default) {
                patch.member_width_mm = style.member_default[0];
                patch.member_depth_mm = style.member_default[1];
              }
              // For gates, the design style IS the gate style — keep them in sync
              // so the backend selects the right hardware/rigging path.
              if (t === "gate") {
                patch.gate_style = v;
                // Force 2 leaves when the user picks a double/bi-fold style
                if (v === "swing_double" || v === "bi_fold") patch.num_leaves = 2;
                if (v === "swing_single" || v === "sliding" || v === "cantilever" || v === "pedestrian") patch.num_leaves = 1;
              }
              upd(patch);
            }}
            options={designStylesFor(t)}
            testid="fab-design-style"
          />
          <Num label="Member width" value={input.member_width_mm} onChange={(v) => upd({ member_width_mm: v })} step="1" min="5" suffix="mm" testid="fab-member-width" />
          <Num label="Member depth" value={input.member_depth_mm} onChange={(v) => upd({ member_depth_mm: v })} step="1" min="5" suffix="mm" testid="fab-member-depth" />
        </div>

        {/* Balustrade */}
        {t === "balustrade" && (
          <div className="grid grid-cols-2 gap-3">
            <Num label="Run length" value={input.length_m} onChange={(v) => upd({ length_m: v })} step="0.1" suffix="m" testid="fab-length" />
            <Num label="Height" value={input.height_mm} onChange={(v) => upd({ height_mm: v })} step="10" suffix="mm" testid="fab-height" />
            <Num label="Post spacing" value={input.post_spacing_mm} onChange={(v) => upd({ post_spacing_mm: v })} step="10" suffix="mm" testid="fab-post-spacing" />
            <Pick label="Infill" value={input.infill_type} onChange={(v) => upd({ infill_type: v })} options={INFILL_TYPES} testid="fab-infill" />
            {input.infill_type === "vertical_bar" && <Num label="Baluster gap" value={input.baluster_spacing_mm} onChange={(v) => upd({ baluster_spacing_mm: v })} step="5" suffix="mm" testid="fab-baluster-gap" />}
            {input.infill_type === "glass" && <Num label="Panel width" value={input.panel_width_mm} onChange={(v) => upd({ panel_width_mm: v })} step="50" suffix="mm" testid="fab-panel-width" />}
            {input.infill_type === "glass" && <Pick label="Glass thickness" value={String(input.glass_thickness_mm || 10)} onChange={(v) => upd({ glass_thickness_mm: parseInt(v, 10) })} options={GLASS_THICKNESSES} testid="fab-glass-thickness" />}
            <div className="col-span-2"><Toggle label="Include handrail" checked={input.has_handrail} onChange={(v) => upd({ has_handrail: v })} testid="fab-handrail" /></div>
          </div>
        )}

        {/* Staircase */}
        {t === "staircase" && (
          <div className="grid grid-cols-2 gap-3">
            <Num label="Total rise (floor→floor)" value={input.total_rise_mm} onChange={(v) => upd({ total_rise_mm: v })} step="10" suffix="mm" testid="fab-total-rise" />
            <Num label="Target riser" value={input.riser_height_mm} onChange={(v) => upd({ riser_height_mm: v })} step="5" suffix="mm" testid="fab-riser" />
            <Num label="Going (tread depth)" value={input.tread_depth_mm} onChange={(v) => upd({ tread_depth_mm: v })} step="5" suffix="mm" testid="fab-going" />
            <Num label="Stair width" value={input.stair_width_mm} onChange={(v) => upd({ stair_width_mm: v })} step="10" suffix="mm" testid="fab-stair-width" />
            <div className="col-span-2"><Toggle label="Include balustrade + handrail" checked={input.has_balustrade} onChange={(v) => upd({ has_balustrade: v })} testid="fab-has-balustrade" /></div>
          </div>
        )}

        {/* Steel frame */}
        {t === "steel_frame" && (
          <div className="grid grid-cols-2 gap-3">
            <Num label="Frame width" value={input.frame_width_mm} onChange={(v) => upd({ frame_width_mm: v })} step="10" suffix="mm" testid="fab-frame-width" />
            <Num label="Frame height" value={input.frame_height_mm} onChange={(v) => upd({ frame_height_mm: v })} step="10" suffix="mm" testid="fab-frame-height" />
            <Num label="Member spacing" value={input.member_spacing_mm} onChange={(v) => upd({ member_spacing_mm: v })} step="10" suffix="mm" testid="fab-member-spacing" />
            <Pick label="Members" value={input.member_orientation} onChange={(v) => upd({ member_orientation: v })} options={FRAME_ORIENTATIONS} testid="fab-orientation" />
          </div>
        )}

        {/* Slat panels — horizontal-slat privacy screens (fabricated in-house) */}
        {t === "slat_panel" && (
          <div className="grid grid-cols-2 gap-3">
            <Num label="Run length" value={input.length_m} onChange={(v) => upd({ length_m: v })} step="0.1" suffix="m" testid="fab-slat-length" />
            <Num label="Panel height" value={input.height_mm} onChange={(v) => upd({ height_mm: v })} step="50" suffix="mm" testid="fab-slat-height" />
            <Num label="Slat blade" value={input.slat_height_mm} onChange={(v) => upd({ slat_height_mm: v })} step="5" suffix="mm" testid="fab-slat-blade" />
            <Num label="Slat gap" value={input.slat_gap_mm} onChange={(v) => upd({ slat_gap_mm: v })} step="1" suffix="mm" testid="fab-slat-gap" />
            <Num label="Post spacing" value={input.post_spacing_mm} onChange={(v) => upd({ post_spacing_mm: v })} step="100" suffix="mm" testid="fab-slat-post-spacing" />
            <div>
              <Label className="stamp-label text-slate-600">Colour / finish</Label>
              <Input value={input.colour || ""} onChange={(e) => upd({ colour: e.target.value })}
                placeholder="e.g. Monument, Gal, Basalt" className="rounded-sm h-9 text-sm mt-1" data-testid="fab-slat-colour" />
            </div>
          </div>
        )}

        {/* Custom panel — free-form fabricated panel */}
        {t === "custom_panel" && (
          <div className="grid grid-cols-2 gap-3">
            <Num label="Panel length" value={input.length_m} onChange={(v) => upd({ length_m: v })} step="0.1" suffix="m" testid="fab-custom-length" />
            <Num label="Panel height" value={input.height_mm} onChange={(v) => upd({ height_mm: v })} step="50" suffix="mm" testid="fab-custom-height" />
            <Num label="Number of panels" value={input.num_panels} onChange={(v) => upd({ num_panels: Math.max(1, Math.round(v)) })} step="1" min="1" suffix="×" testid="fab-custom-num-panels" />
            <Num label="Est. member LM (optional)" value={input.estimated_member_lm} onChange={(v) => upd({ estimated_member_lm: v })} step="0.5" suffix="m" testid="fab-custom-member-lm" />
            <Num label="Labour hours (optional)" value={input.labour_hours} onChange={(v) => upd({ labour_hours: v })} step="0.5" suffix="h" testid="fab-custom-labour-hours" />
            <div>
              <Label className="stamp-label text-slate-600">Colour / finish</Label>
              <Input value={input.colour || ""} onChange={(e) => upd({ colour: e.target.value })}
                placeholder="e.g. Monument, Gal, Basalt" className="rounded-sm h-9 text-sm mt-1" data-testid="fab-custom-colour" />
            </div>
            <div className="col-span-2">
              <Label className="stamp-label text-slate-600">Panel description</Label>
              <Textarea rows={2} value={input.description || ""} onChange={(e) => upd({ description: e.target.value })}
                placeholder="e.g. Laser-cut decorative screen, 3mm sheet, geometric pattern" className="rounded-sm text-sm mt-1" data-testid="fab-custom-description" />
            </div>
          </div>
        )}

        {/* Gate — dedicated calc for swing / double / sliding / bi-fold / cantilever */}
        {t === "gate" && (
          <div className="grid grid-cols-2 gap-3">
            <Num label="Leaf width" value={input.leaf_width_mm} onChange={(v) => upd({ leaf_width_mm: v })} step="10" suffix="mm" testid="fab-gate-leaf-width" />
            <Num label="Leaf height" value={input.leaf_height_mm} onChange={(v) => upd({ leaf_height_mm: v })} step="10" suffix="mm" testid="fab-gate-leaf-height" />
            <Num label="Number of leaves" value={input.num_leaves} onChange={(v) => upd({ num_leaves: Math.max(1, Math.min(4, Math.round(v))) })} step="1" min="1" suffix="×" testid="fab-gate-num-leaves" />
            <Pick label="Infill" value={input.infill_type} onChange={(v) => upd({ infill_type: v })} options={GATE_INFILL_TYPES} testid="fab-gate-infill" />
            {input.infill_type === "bar" && (
              <Num label="Baluster gap" value={input.baluster_spacing_mm} onChange={(v) => upd({ baluster_spacing_mm: v })} step="5" suffix="mm" testid="fab-gate-baluster-gap" />
            )}
            {input.infill_type === "slat" && (
              <>
                <Num label="Slat blade" value={input.slat_height_mm} onChange={(v) => upd({ slat_height_mm: v })} step="5" suffix="mm" testid="fab-gate-slat-blade" />
                <Num label="Slat gap" value={input.slat_gap_mm} onChange={(v) => upd({ slat_gap_mm: v })} step="1" suffix="mm" testid="fab-gate-slat-gap" />
              </>
            )}
            <div className="col-span-2 grid grid-cols-2 gap-3 pt-1">
              <Toggle label="Motorised (auto)"      checked={input.has_motor}       onChange={(v) => upd({ has_motor: v })}      testid="fab-gate-motor" />
              <Toggle label="Key lock cylinder"     checked={input.has_key_lock}    onChange={(v) => upd({ has_key_lock: v })}   testid="fab-gate-key-lock" />
              <Toggle label="Ground drop-bolt"      checked={input.has_drop_bolt}   onChange={(v) => upd({ has_drop_bolt: v })}  testid="fab-gate-drop-bolt" />
              <Toggle label="Self-closing hinges"   checked={input.has_self_close}  onChange={(v) => upd({ has_self_close: v })} testid="fab-gate-self-close" />
            </div>
          </div>
        )}

        {/* Glass */}
        {t === "glass" && (
          <div className="grid grid-cols-2 gap-3">
            <Num label="Run length" value={input.length_m} onChange={(v) => upd({ length_m: v })} step="0.1" suffix="m" testid="fab-length" />
            <Num label="Height" value={input.height_mm} onChange={(v) => upd({ height_mm: v })} step="10" suffix="mm" testid="fab-height" />
            <Num label="Panel width" value={input.panel_width_mm} onChange={(v) => upd({ panel_width_mm: v })} step="50" suffix="mm" testid="fab-panel-width" />
            <Pick label="Glass thickness" value={String(input.glass_thickness_mm || 12)} onChange={(v) => upd({ glass_thickness_mm: parseInt(v, 10) })} options={GLASS_THICKNESSES} testid="fab-glass-thickness" />
            <div className="col-span-2"><Toggle label="Include interlink handrail" checked={input.has_handrail} onChange={(v) => upd({ has_handrail: v })} testid="fab-handrail" /></div>
          </div>
        )}

        <Num label="Quantity (identical builds)" value={input.qty} onChange={(v) => upd({ qty: Math.max(1, Math.round(v)) })} step="1" min="1" suffix="×" testid="fab-qty" />

        <div>
          <Label className="stamp-label text-slate-600">Notes / label</Label>
          <Textarea rows={2} value={input.notes || ""} onChange={(e) => upd({ notes: e.target.value })}
            placeholder="e.g. Rear deck balustrade — powder coat Monument" className="rounded-sm text-sm mt-1" data-testid="fab-notes" />
        </div>
      </Card>

      {/* ---------- Result / blueprint ---------- */}
      <div className="space-y-4" id="fab-result">
        <Card className="rounded-sm border-slate-200 industrial-shadow overflow-hidden">
          <div className="bg-slate-900 text-white p-4 sm:p-5 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="stamp-label text-cyan-400 mb-1 flex items-center gap-1.5"><Boxes size={12} /> Estimated build cost</div>
              <div className="font-display text-3xl font-bold tracking-tight" data-testid="fab-cost-total">{fmtCurrencyExact(result?.cost_total || 0)}</div>
              <div className="text-[11px] text-slate-400 mt-1 truncate">{result?.summary || "Adjust the details on the left"}</div>
            </div>
            {loading && <Loader2 size={18} className="animate-spin text-cyan-400 shrink-0" />}
          </div>
          {result && (
            <div className="p-4 sm:p-5 grid grid-cols-3 gap-3 text-center border-b border-slate-100">
              <div><div className="stamp-label text-slate-400">Material</div><div className="font-mono text-sm font-semibold text-slate-900">{fmtCurrencyExact(result.material_cost)}</div></div>
              <div><div className="stamp-label text-slate-400">Finish</div><div className="font-mono text-sm font-semibold text-slate-900">{fmtCurrencyExact(result.finish_cost)}</div></div>
              <div><div className="stamp-label text-slate-400">Labour ({result.labour_hours}h)</div><div className="font-mono text-sm font-semibold text-slate-900">{fmtCurrencyExact(result.labour_cost)}</div></div>
            </div>
          )}
        </Card>

        {result?.warnings?.length > 0 && (
          <div className="border border-amber-300 bg-amber-50 rounded-sm p-3 space-y-1.5" data-testid="fab-warnings">
            {result.warnings.map((w) => (
              <div key={w} className="flex items-start gap-2 text-xs text-amber-900">
                <AlertTriangle size={13} className="shrink-0 mt-0.5 text-amber-600" /> <span>{w}</span>
              </div>
            ))}
          </div>
        )}

        {/* Fabrication spec — welding, members, glass, stair geometry */}
        {result?.spec && (result.spec.weld || result.spec.members || result.spec.glass || result.spec.angle_deg) && (
          <Card className="rounded-sm border-slate-200 industrial-shadow p-3 sm:p-4 space-y-3" data-testid="fab-spec-card">
            <div className="stamp-label text-cyan-600">Fabrication spec</div>
            {result.spec.members && (
              <div className="grid grid-cols-3 gap-2 text-center" data-testid="fab-spec-members">
                <div><div className="stamp-label text-slate-400">Posts</div><div className="font-mono text-xs font-semibold text-slate-900">{result.spec.members.post}</div></div>
                <div><div className="stamp-label text-slate-400">Rails</div><div className="font-mono text-xs font-semibold text-slate-900">{result.spec.members.rail}</div></div>
                <div><div className="stamp-label text-slate-400">Grade</div><div className="font-mono text-xs font-semibold text-slate-900">{result.spec.members.grade}</div></div>
              </div>
            )}
            {result.spec.angle_deg > 0 && (
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-xs" data-testid="fab-spec-angle">
                <span className="stamp-label text-slate-500">Stair angle</span>
                <span className="font-mono font-semibold text-slate-900">{result.spec.angle_deg}° <span className="text-slate-400 font-normal">(AS 1657: 20–45°)</span></span>
              </div>
            )}
            {result.spec.weld && (
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="fab-spec-weld">
                <div className="stamp-label text-slate-500 mb-1.5">Welding — {result.spec.weld.process}</div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div><div className="text-[9px] uppercase tracking-wider text-slate-400">Joints</div><div className="font-mono text-sm font-semibold">{result.spec.weld.joints}</div></div>
                  <div><div className="text-[9px] uppercase tracking-wider text-slate-400">Weld length</div><div className="font-mono text-sm font-semibold">{result.spec.weld.weld_lm}m</div></div>
                  <div><div className="text-[9px] uppercase tracking-wider text-slate-400">Wire/rod</div><div className="font-mono text-sm font-semibold">{result.spec.weld.wire_kg}kg</div></div>
                  <div><div className="text-[9px] uppercase tracking-wider text-slate-400">Arc time</div><div className="font-mono text-sm font-semibold">{result.spec.weld.weld_hours}h</div></div>
                </div>
                <div className="text-[10px] text-slate-400 mt-1.5">{result.spec.weld.consumable} · {result.spec.weld.gas} ~{result.spec.weld.gas_l}L</div>
              </div>
            )}
            {result.spec.glass && (
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="fab-spec-glass">
                <div className="stamp-label text-slate-500 mb-1.5">Glass spec</div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-[9px] uppercase tracking-wider text-slate-400">Thickness</div><div className="font-mono text-sm font-semibold">{result.spec.glass.thickness_mm}mm</div></div>
                  <div><div className="text-[9px] uppercase tracking-wider text-slate-400">Panel size</div><div className="font-mono text-sm font-semibold">{result.spec.glass.panel_w_mm}×{result.spec.glass.panel_h_mm}</div></div>
                  <div><div className="text-[9px] uppercase tracking-wider text-slate-400">Weight</div><div className="font-mono text-sm font-semibold">{result.spec.glass.panel_weight_kg}kg/panel</div></div>
                </div>
                <div className="text-[10px] text-slate-400 mt-1.5">{result.spec.glass.grade} · {result.spec.glass.fixing} · total {result.spec.glass.total_weight_kg}kg</div>
              </div>
            )}
          </Card>
        )}

        {/* Blueprint */}
        <Card className="rounded-sm border-slate-200 industrial-shadow p-3 sm:p-4">
          <div className="stamp-label text-cyan-600 mb-2">Blueprint (indicative)</div>
          <FabricationBlueprint fabType={t} spec={result?.spec} />
        </Card>

        {/* BOM */}
        {result?.bom?.length > 0 && (
          <Card className="rounded-sm border-slate-200 industrial-shadow overflow-hidden" data-testid="fab-bom">
            <div className="stamp-label text-cyan-600 px-4 pt-4 pb-2">Bill of materials</div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-y border-slate-200">
                <tr className="text-left stamp-label text-slate-500">
                  <th className="px-4 py-2">Item</th>
                  <th className="px-2 py-2 text-right">Qty</th>
                  <th className="px-2 py-2">Unit</th>
                  <th className="px-4 py-2 text-right">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {result.bom.map((b, i) => (
                  <tr key={i} className="hover:bg-slate-50/50">
                    <td className="px-4 py-2 text-slate-800">{b.item}</td>
                    <td className="px-2 py-2 text-right font-mono">{b.qty}</td>
                    <td className="px-2 py-2 text-slate-500 text-xs">{b.unit}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold">{fmtCurrencyExact(b.line_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
}
